import React, { useState, useEffect, useRef } from 'react';
import { ModalHeader, ModalBody, Row, Col, FormGroup, Label } from 'reactstrap';
import { Form, Field, Input } from '@availity/form';
import * as yup from 'yup';
import createNotification from '../../../../utils/createNotification';
import Affiliation from '../../../Practices/Affiliation';
import Loader from 'react-loaders';
import _ from 'lodash';
import AssociateRepresentativeModal from '../../PracticeManagementPMG/Modal/AssociateRepresentativeModal';
import { YEARS_OF_PRACTICE_OWNERSHIP_DROPDOWN_ITEMS } from '../../../../utils/practiceProfileConstants';
import Helpers from '../../../../utils/helper';
import Rodal from '../../../../components/Rodal/Rodal';
import { USER_ROLES } from '../../../../utils/constants';
import './MembershipProfileModal.scss';
import { DirectButton } from '../../../../direct/direct-button/direct-button';
import { useStateSelector } from '../../../../store/selectors';
import { PmgMemberProfile } from '../../../../models/PmgMembershipProfile/PmgMemberProfile';
import { PmgMembershipProfile } from '../../../../models/PmgMembershipProfile/PmgMembershipProfile';
import PmgMembershipProfileLocations from './PmgMembershipProfileComponents/PmgMembershipProfileLocations';
import PmgMembershipGeneralProfile from './PmgMembershipProfileComponents/PmgMembershipGeneralProfile';
import PmgMembershipProfileAffiliatedPracticeRepresentativeSection from './PmgMembershipProfileComponents/PmgMemberShipProfileAffiliatedPracticeRepresentativeSection';
import { getPracticeLocationDataArraySchema } from '../../../../utils/practiceLocationDataValidation';
import {
    DataSynchronizationValidator,
    SyncField,
} from '../../../../components/DataSynchronizationValidator/DataSynchronizationValidator';
import { useServiceFocuses } from '../../../../custom-hooks/useServiceFocuses';
import {
    getOtherFocus,
    isOtherFocus,
} from '../../../UserPages/Login/Modals/PracticeRegistrationForm/utils';

// Interfaces
interface MembershipProfileModalProps {
    isVisible: boolean;
    setIsVisible: (visible: boolean) => void;
    groupId: number;
    practiceId?: number;
    reloadTable?: () => void;
    isTableMode?: boolean;
    updateData?: () => void;
    setIsMemberApplyed?: (applied: boolean) => void;
    userId?: number;
    showGeneralIfMainNotExist?: boolean;
    onDirectOpened?: () => void;
    onPmgRepresentativeSet?: (() => void) | null;
}

interface AssociateMember {
    id: number;
    name: string;
    practices?: AssociatePractice[];
}

interface AssociatePractice {
    practiceId: number;
    roleId: number;
}

const spinner = <Loader active={true} type="ball-scale-multiple" />;

const MembershipProfileModal: React.FC<MembershipProfileModalProps> = ({
    isVisible,
    setIsVisible,
    groupId,
    practiceId,
    reloadTable,
    isTableMode,
    updateData,
    setIsMemberApplyed,
    userId,
    showGeneralIfMainNotExist,
    onDirectOpened,
    onPmgRepresentativeSet = null,
}) => {
    const header = userId ? 'Member Info' : 'My Membership Profile';
    const zipCodeCache = useRef<Record<string, boolean>>({}).current;

    const axios = useStateSelector((state) => state.core.axios);
    const serviceFocuses = useServiceFocuses();
    const [animation] = useState<string>('slideTop');
    const [isEditVisible, setIsEditVisible] = useState<boolean>(false);
    const [isMembershipProfileLoading, setisMembershipProfileLoading] =
        useState<boolean>(false);
    const [membershipData, setMembershipData] =
        useState<PmgMembershipProfile>();
    const [isRepresentativeVisible, setIsRepresentativeVisible] =
        useState<boolean>(false);
    const [affiliates, setAffiliates] = useState<any[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [isGeneralProfileVisible, setIsGeneralProfileVisible] =
        useState<boolean>(false);
    const [loaderMessage, setLoaderMessage] = useState<string>('Loading...');
    const [associateMembers, setAssociateMembers] = useState<AssociateMember[]>(
        []
    );
    const [selectedAssociate, setSelectedAssociate] = useState<
        number | undefined
    >();
    const [memberProfile, setMemberProfile] = useState<
        PmgMemberProfile | undefined
    >();
    const [isAssociateModalVisible, setIsAssociateModalVisible] =
        useState<boolean>(false);
    const formRef = useRef<any>(null);
    const [syncTrigger, setSyncTrigger] = useState(0);

    const isUserAdmin =
        JSON.parse(localStorage.getItem('loggedInUserSummary') || '{}')
            .roleId === USER_ROLES.ADMIN;
    const isUserAssociate =
        JSON.parse(localStorage.getItem('loggedInUserSummary') || '{}')
            .roleId === USER_ROLES.PRACTICE_SECONDARY_USER;

    useEffect(() => {
        getMembershipProfile();
    }, [isVisible]);

    const getMembershipProfile = () => {
        setLoaderData(true, '');
        setisMembershipProfileLoading(true);
        axios
            .get(
                userId
                    ? `/api/Group/GetMembershipProfile/${userId}?groupId=${groupId}${
                          practiceId ? `&practiceId=${practiceId}` : ''
                      }`
                    : `/api/Group/GetMembershipProfile?groupId=${groupId}${
                          groupId == 0 ? `&practiceId=${practiceId}` : ''
                      }`
            )
            .then((practiceDataResponse) => {
                if (practiceDataResponse.data.isError) {
                    createNotification(
                        'An error occured while fetching membership profile details',
                        'error'
                    );
                } else {
                    setMembershipData(practiceDataResponse.data.data);
                    if (!isUserAssociate) {
                        setisMembershipProfileLoading(true);
                        axios
                            .get(
                                `/api/Associate/practice/${practiceDataResponse.data.data.practiceId}`
                            )
                            .then((membersResponse) => {
                                if (practiceDataResponse.data.isError) {
                                    createNotification(
                                        'An error occured while fetching membership profile details',
                                        'error'
                                    );
                                } else {
                                    let members = membersResponse.data.data;
                                    let empty = { name: 'None', id: 0 };
                                    members.unshift(empty);
                                    setAssociateMembers(members);
                                    setSelectedAssociate(
                                        practiceDataResponse.data.data
                                            .practiceRepresentativeId
                                    );

                                    setisMembershipProfileLoading(false);
                                    createNotification(
                                        practiceDataResponse.data.message,
                                        'success'
                                    );
                                }
                            })
                            .finally(() => {
                                setisMembershipProfileLoading(false);
                            });
                    } else {
                        setisMembershipProfileLoading(false);
                    }
                }
            })
            .catch(function () {
                if (showGeneralIfMainNotExist) {
                    axios
                        .get(
                            `/api/Group/GetGeneralMemberProfile?groupId=${groupId}&userId=${userId}`
                        )
                        .then((result) => {
                            setMemberProfile(result.data);
                            setIsGeneralProfileVisible(true);
                            setisMembershipProfileLoading(false);
                            createNotification(
                                'Emeritus member profile fetched successfully.',
                                'success'
                            );
                        })
                        .catch(() => {
                            createNotification(
                                'An error occured while fetching emeritus member profile',
                                'error'
                            );
                        });
                } else {
                    createNotification(
                        'An error occured while fetching membership profile details',
                        'error'
                    );
                }
            })
            .finally(function () {
                setLoaderData(false, '');
            });
    };

    const setLoaderData = (loader: boolean, msg: string) => {
        setLoader(loader);
        setLoaderMessage(msg);
    };

    const saveMembershipData = (values: any) => {
        setLoaderData(true, 'Saving Membership Data...');
        setisMembershipProfileLoading(true);
        setIsEditVisible(false);

        const dataToSave = {
            ...membershipData,
            ...values,
        };

        axios
            .post('/api/Group/UpdateMembershipProfile', dataToSave)
            .then((response) => {
                if (response.status === 200) {
                    setMembershipData(dataToSave);
                    setisMembershipProfileLoading(false);
                    setSyncTrigger((prev) => prev + 1);
                    createNotification(response.data.message, 'success');
                } else if (response?.data?.isError) {
                    createNotification(
                        `An error occured while saving membership profile details${
                            response.data.message
                                ? ` - ${response.data.message}`
                                : ''
                        }`,
                        'error'
                    );
                } else {
                    createNotification(
                        'An error ocurred while saving membership profile details.',
                        'error'
                    );
                }

                getMembershipProfile();
            })
            .catch(function () {
                createNotification(
                    'An error ocurred while saving membership profile details.',
                    'error'
                );
            })
            .finally(function () {
                setLoaderData(false, '');
            });
    };

    function getLoaderHtml() {
        return (
            <div
                className="loader-container"
                style={{ height: '100%', width: '100%' }}>
                <div className="loader-container-inner">
                    <br />
                    <div className="text-center">{spinner}</div>
                    <h6 className="mt-5">
                        {loaderMessage ? loaderMessage : 'Loading...'}
                    </h6>
                </div>
            </div>
        );
    }

    function setAssociateRepresentative(memberId: number) {
        const sendObject = {
            memberId: memberId,
            groupId: groupId,
            practiceId: membershipData?.practiceId,
        };
        axios
            .post(
                isUserAdmin
                    ? 'api/AssociateRepresentative'
                    : 'api/AssociateRepresentativeRequests',
                sendObject
            )
            .then((response) => {
                if (response.data) {
                    if (!response.data.isError) {
                        if (isTableMode) {
                            if (updateData) {
                                updateData();
                            }
                            if (setIsMemberApplyed) {
                                setIsMemberApplyed(true);
                            }
                        }
                        createNotification(
                            'Affiliated Practice Representative has been successfully set.',
                            'success'
                        );
                        setIsAssociateModalVisible(false);
                        getMembershipProfile();
                        if (onPmgRepresentativeSet) {
                            onPmgRepresentativeSet();
                        }
                        if (reloadTable) {
                            reloadTable();
                        }
                    } else {
                        createNotification(response.data.message, 'error');
                    }
                } else if (response?.data?.message) {
                    createNotification(response.data.message, 'error');
                }
            })
            .catch((error: any) => {
                createNotification(error.message, 'error');
            });
    }

    const fteDvmsRange = Helpers.getFteDvmsRange();

    // Validation schema for the form
    const validationSchema = yup.object().shape({
        name: yup.string().trim().required('Practice name is required'),
        practiceUrl: yup.string().trim().required('Practice URL is required'),
        practiseTypeId: yup
            .number()
            .test(
                'practiseTypeId-required',
                'Service focus is required',
                function (value) {
                    return value !== 0 && value !== null && value !== undefined;
                }
            )
            .required('Service focus is required'),
        otherPracticeType: yup.string().when('practiseTypeId', {
            is: getOtherFocus(serviceFocuses).id,
            then: (schema) =>
                schema.required('Please specify the service focus type'),
            otherwise: (schema) => schema.notRequired(),
        }),
        mostRecentCYRevenue: yup
            .number()
            .min(0, 'Most Recent CY Revenue must be greater than 0')
            .required('Most Recent CY Revenue is required'),
        fullTimeEquivalentW2DVMs: yup
            .number()
            .min(0, 'Full Time Equivalent W2 DVMs must be greater than 0'),
        practiceLocationData: getPracticeLocationDataArraySchema(
            axios,
            zipCodeCache,
            {
                includePhone: false,
                includeWebsite: false,
                includeFteDvm: false,
            },
            serviceFocuses
        ),
    });

    const renderPracticeNameAndMemberSince = () => (
        <div className="dynamic_data_content">
            {isEditVisible ? (
                <>
                    <FormGroup>
                        <Field
                            type="text"
                            label="Practice Name"
                            name="name"
                            disabled={!isEditVisible}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Field
                            type="text"
                            label="Practice URL"
                            name="practiceUrl"
                            disabled={!isEditVisible}
                        />
                    </FormGroup>
                </>
            ) : (
                <>
                    <div className="dynamic_data">
                        <label>Practice Name</label>
                        <span className="profile_data">
                            {membershipData?.name}
                        </span>
                    </div>
                    <div className="dynamic_data">
                        <label>Practice URL</label>
                        <span className="profile_data">
                            {membershipData?.practiceUrl}
                        </span>
                    </div>
                </>
            )}
            <div className="dynamic_data">
                <label>PMG 1 Member Since</label>
                <span className="profile_data">
                    {membershipData?.groupMemberSince}
                </span>
            </div>
        </div>
    );

    const renderPrimaryPracticeRepresentative = () => (
        <div className="dynamic_data_content">
            <h6>Primary Practice Representative</h6>
            <div className="dynamic_data name-container">
                {isEditVisible ? (
                    <>
                        <label>Name</label>
                        <input
                            type="text"
                            readOnly
                            className="form-control"
                            value={membershipData?.practiceOwnerName}
                        />
                    </>
                ) : (
                    <>
                        <label>Name</label>
                        <div className="name-container">
                            <span className="profile_data">
                                {membershipData?.practiceOwnerName}
                            </span>
                            <DirectButton
                                clickCallback={() => {
                                    setIsVisible(false);
                                    if (onDirectOpened) {
                                        onDirectOpened();
                                    }
                                }}
                                memberId={membershipData?.practiceOwnerId}
                                size="small"
                            />
                        </div>
                    </>
                )}
            </div>
            <div className="dynamic_data">
                <label>Ownership Period</label>
                {isEditVisible ? (
                    <input
                        type="text"
                        readOnly
                        className="form-control"
                        value={
                            YEARS_OF_PRACTICE_OWNERSHIP_DROPDOWN_ITEMS.find(
                                (y) =>
                                    y.id ==
                                    membershipData?.practiceOwnerOwnershipPeriod
                            )?.name ?? ''
                        }
                    />
                ) : (
                    <span className="profile_data">
                        {YEARS_OF_PRACTICE_OWNERSHIP_DROPDOWN_ITEMS.find(
                            (y) =>
                                y.id ==
                                membershipData?.practiceOwnerOwnershipPeriod
                        )?.name ?? ''}
                    </span>
                )}
            </div>
            <div className="dynamic_data">
                <label>Mobile Phone</label>
                {isEditVisible ? (
                    <input
                        type="text"
                        className="form-control"
                        readOnly
                        value={Helpers.getFormattedPhoneNumberWithoutPrefix(
                            membershipData?.practiceOwnerPhone
                        )}
                    />
                ) : (
                    <span className="profile_data">
                        {Helpers.getFormattedPhoneNumberWithoutPrefix(
                            membershipData?.practiceOwnerPhone
                        )}
                    </span>
                )}
            </div>
            <div className="dynamic_data">
                <label>Email Address</label>
                {isEditVisible ? (
                    <input
                        type="text"
                        className="form-control"
                        readOnly
                        value={membershipData?.practiceOwnerEmail || ''}
                    />
                ) : (
                    <span className="profile_data">
                        {membershipData?.practiceOwnerEmail}
                    </span>
                )}
            </div>
        </div>
    );

    const handleApplyToChange = () => {
        setIsAssociateModalVisible(true);
    };

    const handleDirectButtonClick = () => {
        setIsVisible(false);
        if (onDirectOpened) {
            onDirectOpened();
        }
    };

    const renderServiceFocusFields = () => {
        return (
            <>
                {isEditVisible ? (
                    <FormGroup>
                        <label>Service Focus</label>
                        <Input
                            type="select"
                            name="practiseTypeId"
                            disabled={!isEditVisible}
                            onChange={(event) => {
                                setMembershipData({
                                    ...membershipData,
                                    practiseTypeId: Number(event.target.value),
                                });
                            }}>
                            {[{ id: 0, name: 'Select' }, ...serviceFocuses].map(
                                (item) => (
                                    <option
                                        value={item.id}
                                        key={`practiceFocus${item.id}`}>
                                        {item.name}
                                    </option>
                                )
                            )}
                        </Input>
                    </FormGroup>
                ) : (
                    <div className="dynamic_data">
                        <label>Service Focus</label>
                        <span className="profile_data">
                            {isOtherFocus(
                                serviceFocuses,
                                membershipData?.practiseTypeId
                            )
                                ? membershipData?.otherPracticeType ||
                                  getOtherFocus(serviceFocuses)?.name ||
                                  ''
                                : serviceFocuses.find(
                                      (item) =>
                                          item.id ===
                                          membershipData?.practiseTypeId
                                  )?.name || ''}
                        </span>
                    </div>
                )}
                {isOtherFocus(
                    serviceFocuses,
                    membershipData?.practiseTypeId
                ) && (
                    <>
                        {isEditVisible ? (
                            <FormGroup>
                                <Label>Other Service Focus</Label>
                                <Field
                                    type="text"
                                    name="otherPracticeType"
                                    placeholder="Enter service focus type"
                                    disabled={!isEditVisible}
                                />
                            </FormGroup>
                        ) : (
                            <></>
                        )}
                    </>
                )}
            </>
        );
    };

    const renderPracticeLocationsData = () => {
        return (
            <div className="dynamic_data_content">
                <h6 className={isEditVisible ? '' : 'pt-0'}>
                    Practice Information
                </h6>
                <div className="dynamic_data">
                    <label># FTE DVMs</label>
                    <span className="profile_data fte-dvm-count">
                        {membershipData?.practiceFteDvmNumber || 0}
                    </span>
                </div>
                {isEditVisible ? (
                    <FormGroup>
                        <Field
                            type="text"
                            label="Most Recent CY Revenue"
                            name="mostRecentCYRevenue"
                            disabled={!isEditVisible}
                        />
                    </FormGroup>
                ) : (
                    <div className="dynamic_data">
                        <label>Most Recent CY Revenue</label>
                        <span className="profile_data">
                            {membershipData?.mostRecentCYRevenue || 'N/A'}
                        </span>
                    </div>
                )}

                {renderServiceFocusFields()}

                <h6>Practice Locations</h6>
                <PmgMembershipProfileLocations
                    practiceLocationData={
                        membershipData?.practiceLocationData || []
                    }
                    readOnlyView={!isEditVisible}
                />
            </div>
        );
    };

    const renderEditButtons = () => (
        <Row>
            <Col lg={12}>
                <div className="form-submit-btn input-group">
                    <button
                        type="button"
                        className="btn mr-2"
                        onClick={() => {
                            setIsEditVisible(false);
                            formRef.current?.handleReset();
                        }}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-submit">
                        Save
                    </button>
                </div>
            </Col>
        </Row>
    );

    const renderMembershipForm = () => (
        <Form
            innerRef={formRef}
            onSubmit={saveMembershipData}
            initialValues={membershipData}
            validationSchema={isEditVisible ? validationSchema : undefined}>
            <div
                className={
                    isEditVisible
                        ? 'edit_membership_profile'
                        : 'pmg_placement_popup'
                }>
                <Row>
                    <Col lg={6} md={6} sm={12}>
                        {renderPracticeNameAndMemberSince()}
                        {renderPrimaryPracticeRepresentative()}
                        <PmgMembershipProfileAffiliatedPracticeRepresentativeSection
                            membershipData={membershipData}
                            isEditVisible={isEditVisible}
                            isUserAdmin={isUserAdmin}
                            onApplyToChange={handleApplyToChange}
                            onDirectButtonClick={handleDirectButtonClick}
                        />
                        <div className="dynamic_data">
                            <label>Affiliations</label>
                            <span className="profile_data">
                                {membershipData?.noOfAffilations || 0}{' '}
                                <a
                                    href="#0"
                                    onClick={() => {
                                        setIsRepresentativeVisible(true);
                                    }}>
                                    <i>View Affiliations</i>
                                </a>
                            </span>
                        </div>
                    </Col>
                    <Col lg={6} md={6} sm={12}>
                        {renderPracticeLocationsData()}
                    </Col>
                </Row>
                {isEditVisible && renderEditButtons()}
            </div>
        </Form>
    );

    const membershipFields = (
        <>
            {loader && getLoaderHtml()}
            {!loader &&
                !isGeneralProfileVisible &&
                !isMembershipProfileLoading &&
                renderMembershipForm()}
            {!loader &&
                isGeneralProfileVisible &&
                !isMembershipProfileLoading &&
                memberProfile && (
                    <PmgMembershipGeneralProfile
                        memberProfile={memberProfile}
                    />
                )}
        </>
    );

    const popupFormat = (
        <Rodal
            visible={true}
            animation={animation}
            showMask={true}
            width={isGeneralProfileVisible ? 464 : 978}
            showCloseButton={true}
            onClose={() => {
                setIsVisible(false);
            }}
            className={
                isGeneralProfileVisible
                    ? 'general-profile-modal top membership_profile_popup'
                    : 'top membership_profile_popup'
            }>
            <ModalHeader
                className={
                    isGeneralProfileVisible ? 'general-profile-header' : ''
                }>
                {header}
                {!isEditVisible &&
                    !isMembershipProfileLoading &&
                    ((!userId && !isUserAssociate) || isUserAdmin) && (
                        <a
                            href="#0"
                            className="blue_btn"
                            onClick={() => {
                                setIsEditVisible(true);
                            }}>
                            Edit Data
                        </a>
                    )}
            </ModalHeader>
            <ModalBody>
                {isMembershipProfileLoading
                    ? getLoaderHtml()
                    : membershipFields}
            </ModalBody>
        </Rodal>
    );

    const tableFormat = isMembershipProfileLoading ? (
        getLoaderHtml()
    ) : (
        <div className="top membership_profile_popup">
            <ModalHeader>
                {header}
                {!isEditVisible &&
                    !isMembershipProfileLoading &&
                    ((!userId && !isUserAssociate) || isUserAdmin) && (
                        <a
                            href="#0"
                            className="blue_btn"
                            onClick={() => {
                                setIsEditVisible(true);
                            }}>
                            Edit Data
                        </a>
                    )}
            </ModalHeader>
            <ModalBody>{membershipFields}</ModalBody>
        </div>
    );
    return (
        <>
            <div className="membership-profile-modal">
                <>
                    {isTableMode ? tableFormat : popupFormat}
                    {isRepresentativeVisible && (
                        <Rodal
                            visible={isRepresentativeVisible}
                            animation={animation}
                            showMask={true}
                            width={500}
                            showCloseButton={true}
                            onClose={() => {
                                setIsRepresentativeVisible(false);
                            }}
                            className="top membership_profile_popup pmg_placement_popup multistep_popup">
                            <ModalHeader>Affiliations</ModalHeader>
                            <ModalBody>
                                <Affiliation
                                    data={{
                                        id: membershipData?.practiceId,
                                        affiliates,
                                        setAffiliates,
                                    }}
                                    viewApplication={!isEditVisible}
                                />
                            </ModalBody>
                        </Rodal>
                    )}
                </>

                <AssociateRepresentativeModal
                    isVisible={isAssociateModalVisible}
                    setIsVisible={setIsAssociateModalVisible}
                    associateMembers={associateMembers}
                    selectedAssociate={selectedAssociate}
                    onSubmit={setAssociateRepresentative}
                    practiceId={practiceId}></AssociateRepresentativeModal>
                {membershipData && (
                    <DataSynchronizationValidator
                        practiceId={membershipData.practiceId}
                        fteDvm={0}
                        ltmRevenue={membershipData.mostRecentCYRevenue || 0}
                        sync={syncTrigger}
                        fieldsToSync={[SyncField.LtmRevenue]}
                    />
                )}
            </div>
        </>
    );
};
export default MembershipProfileModal;
